import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";

import IconButton from "../button/IconButton";
import GenerativeAudioThumbnail from "../media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import { formatTimecode } from "../../utils/timecode";
import { findActiveCueIndex, parseSubtitleCues } from "../../utils/subtitles";
import type { AudioPlayerBarTrack } from "./AudioPlayerBar";

/**
 * 鑑賞モード（フルスクリーン再生オーバーレイ）。
 *
 * AudioPlayerBar の３点メニュー「鑑賞モードで観る」から開く。音声の実体
 * （<audio> 要素）はバー側が持ち続け、ここは表示とコントロールだけを担う
 * （バーはオーバーレイの背後に隠れるが破棄しない）。
 *
 * - 黒背景 + ジャケット（無ければ生成サムネイル）+ 作品名 / アーティスト名
 * - 作品に登録された字幕（subtitle_text）を再生位置に合わせてテロップ表示
 * - 字幕位置は 下 / 中央 / 左 / 右 を選択可（左右は縦書き）。選択は保存される
 * - 可能なら Fullscreen API で本当の全画面にする（非対応環境は固定レイヤーで代替）
 * - 再生中はコントロールを自動で隠す（ポインター操作 / 一時停止で再表示）
 * - Esc で終了、Space で再生 / 一時停止
 */

type SubtitlePosition = "bottom" | "center" | "left" | "right";

/** 字幕位置の保存キー（既存キーの `soremo:camelCase` 形式に合わせる） */
const POSITION_STORAGE_KEY = "soremo:appreciationSubtitlePosition";

const POSITION_OPTIONS: { value: SubtitlePosition; label: string }[] = [
  { value: "bottom", label: "下" },
  { value: "center", label: "中央" },
  { value: "left", label: "左" },
  { value: "right", label: "右" },
];

const isSubtitlePosition = (value: unknown): value is SubtitlePosition =>
  value === "bottom" || value === "center" || value === "left" || value === "right";

interface AppreciationModeProps {
  track: AudioPlayerBarTrack;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onTogglePlay: () => void;
  /** 秒指定シーク（バー側の audio 要素を動かす）。 */
  // eslint-disable-next-line no-unused-vars
  onSeek: (seconds: number) => void;
  /** 生成サムネイル用（バーの再生系をそのまま渡す）。 */
  getCurrentTime: () => number;
  readSpectrum: React.ComponentProps<typeof GenerativeAudioThumbnail>["readSpectrum"];
  onClose: () => void;
}

// ===== Styled =====

const Overlay = styled.div<{ $hideCursor: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1200; /* Modal(1000) より上の鑑賞専用レイヤー */
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  ${({ $hideCursor }) => ($hideCursor ? "cursor: none;" : "")}
`;

/** ジャケットをぼかして敷く背景（Apple Music 風）。 */
const Backdrop = styled.div`
  position: absolute;
  inset: -64px;
  background-position: center;
  background-size: cover;
  filter: blur(64px) brightness(0.3);
  pointer-events: none;
`;

const Stage = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  max-width: 90vw;
`;

const artworkSize = css`
  width: min(52vh, 72vw);
  max-width: 440px;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
`;

const Artwork = styled.img`
  ${artworkSize}
  object-fit: cover;
`;

/** 生成サムネイルの置き場（中身は absolute で敷かれる）。 */
const GenerativeBox = styled.div`
  ${artworkSize}
  position: relative;
  overflow: hidden;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: min(84vw, 720px);
  text-align: center;
`;

/** SP と判定する上限幅。プロジェクト他箇所（Preview 等）の境界に合わせる。 */
const SUBTITLE_SP_MAX_WIDTH_PX = 768;

/**
 * 横書き（下 / 中央）テロップの SP 調整。狭い画面では 82vw の折返し幅では
 * 行が短くなりすぎて行数が増え、ジャケットや作品名と縦に交差しやすい。
 * ビューポートをほぼ使い切る幅にして 1 行あたりの文字数を稼ぐ
 * （結果として行数が減り、タイトルとの重なりも軽くなる）。
 * letter-spacing も詰めて、同じ幅でさらに文字数を入れる。
 */
const horizontalTelopOnSp = css`
  @media (max-width: ${SUBTITLE_SP_MAX_WIDTH_PX}px) {
    max-width: 100vw;
    width: 100vw;
    box-sizing: border-box;
    padding-left: max(8px, env(safe-area-inset-left));
    padding-right: max(8px, env(safe-area-inset-right));
    letter-spacing: 0.02em;
  }
`;

/** 縦書き（左 / 右）テロップの SP 調整。縦方向も画面をより使い切る。 */
const verticalTelopOnSp = css`
  @media (max-width: ${SUBTITLE_SP_MAX_WIDTH_PX}px) {
    max-height: 88vh;
    letter-spacing: 0.02em;
  }
`;

/**
 * テロップ本体。位置ごとの配置は $position で切り替える（左右は縦書き）。
 * 背景は敷かず（画面自体が暗いため）、ぼかし背景の上でも読めるよう
 * テキストシャドウのみで縁取る。
 */
const SubtitleTelop = styled.div<{ $position: SubtitlePosition }>`
  position: absolute;
  z-index: 3;
  color: #fff;
  font-family: "A+mfCv-AXISラウンド 50 R StdN", "M PLUS 1p", sans-serif;
  white-space: pre-wrap;
  line-height: 1.75;
  letter-spacing: 0.06em;
  text-shadow:
    0 1px 6px rgba(0, 0, 0, 0.9),
    0 0 24px rgba(0, 0, 0, 0.6);
  pointer-events: none;

  ${({ $position }) => {
    switch ($position) {
      case "center":
        return css`
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          max-width: min(82vw, 720px);
          text-align: center;
          font-size: clamp(16px, 2.8vw, 26px);
          ${horizontalTelopOnSp}
        `;
      case "left":
        return css`
          writing-mode: vertical-rl;
          left: max(20px, 4vw);
          top: 50%;
          transform: translateY(-50%);
          max-height: 76vh;
          font-size: clamp(15px, 2.2vw, 21px);
          ${verticalTelopOnSp}
        `;
      case "right":
        return css`
          writing-mode: vertical-rl;
          right: max(20px, 4vw);
          top: 50%;
          transform: translateY(-50%);
          max-height: 76vh;
          font-size: clamp(15px, 2.2vw, 21px);
          ${verticalTelopOnSp}
        `;
      case "bottom":
      default:
        return css`
          left: 50%;
          transform: translateX(-50%);
          bottom: calc(132px + env(safe-area-inset-bottom));
          max-width: min(82vw, 720px);
          text-align: center;
          font-size: clamp(15px, 2.4vw, 22px);
          ${horizontalTelopOnSp}
        `;
    }
  }}
`;

const TopControls = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(16px + env(safe-area-inset-top));
  right: 20px;
  z-index: 5;
  transition: opacity 0.3s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

const ControlLayer = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  justify-content: center;
  padding: 0 16px calc(20px + env(safe-area-inset-bottom));
  transition: opacity 0.3s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

/** AudioPlayerBar のパネルと同じ流儀のダークグラス。 */
const ControlPanel = styled.div`
  width: 100%;
  max-width: 720px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(20, 20, 20, 0.55);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  padding: 12px 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SubtitleControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const PositionChip = styled.button.attrs({ className: "bodytext-11" })<{
  $active: boolean;
}>`
  border: 1px solid
    ${({ $active }) => ($active ? "var(--soremo-blue)" : "rgba(255, 255, 255, 0.3)")};
  background: ${({ $active }) =>
    $active ? "rgba(56, 116, 255, 0.28)" : "transparent"};
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255, 255, 255, 0.72)")};
  border-radius: 999px;
  padding: 2px 10px;
  line-height: 1.6;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  @media (hover: hover) {
    &:hover {
      border-color: var(--soremo-blue);
      color: #fff;
    }
  }
`;

// シークバー（AudioPlayerBar と同構造の白系配色）。

const SeekBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  touch-action: none;
`;

const SeekBarTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 1.5px;
  background: rgba(255, 255, 255, 0.24);
  overflow: hidden;
`;

const SeekBarFill = styled.div`
  height: 100%;
  background: #fff;
  border-radius: 1.5px;
`;

const SeekBarThumb = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const TimeRow = styled.div.attrs({ className: "bodytext-11" })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.64);
  line-height: 1;
  user-select: none;
`;

// ===== 本体 =====

const AppreciationMode: React.FC<AppreciationModeProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onTogglePlay,
  onSeek,
  getCurrentTime,
  readSpectrum,
  onClose,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const seekBarRef = useRef<HTMLDivElement>(null);

  // イベントリスナーを張り直さずに最新のハンドラを呼ぶための ref。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onTogglePlayRef = useRef(onTogglePlay);
  onTogglePlayRef.current = onTogglePlay;

  // ===== 字幕 =====
  const cues = useMemo(() => parseSubtitleCues(track.subtitleText), [track.subtitleText]);
  const hasCues = useMemo(() => cues.some((cue) => cue.text !== ""), [cues]);
  const [subtitleEnabled, setSubtitleEnabled] = useState(hasCues);

  // トラックが切り替わったら「字幕があればオン」に戻す（キュー再生時の既定挙動）。
  useEffect(() => {
    setSubtitleEnabled(hasCues);
  }, [track.id, hasCues]);

  const [position, setPosition] = useState<SubtitlePosition>(() => {
    try {
      const stored = window.localStorage.getItem(POSITION_STORAGE_KEY);
      return isSubtitlePosition(stored) ? stored : "bottom";
    } catch {
      return "bottom";
    }
  });

  const handleSelectPosition = useCallback((next: SubtitlePosition) => {
    setPosition(next);
    try {
      window.localStorage.setItem(POSITION_STORAGE_KEY, next);
    } catch {
      // localStorage が使えない環境では選択をセッション内のみ保持する
    }
  }, []);

  const activeCueText = useMemo(() => {
    if (!subtitleEnabled) return "";
    const index = findActiveCueIndex(cues, currentTime);
    return index >= 0 ? cues[index].text : "";
  }, [subtitleEnabled, cues, currentTime]);

  // ===== フルスクリーン =====
  // 開いたら可能な限り本当の全画面へ。ブラウザ操作（Esc 等）で全画面が解除
  // されたら鑑賞モード自体も終了する。非対応環境（iOS Safari 等）は固定
  // レイヤーのまま動作する。
  const enteredFullscreenRef = useRef(false);
  useEffect(() => {
    const el = overlayRef.current;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        enteredFullscreenRef.current = true;
      } else if (enteredFullscreenRef.current) {
        onCloseRef.current();
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // 背面ページのスクロールを止める（Modal と同じ流儀）。
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc で終了 / Space で再生・一時停止。
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
      } else if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        onTogglePlayRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ===== コントロールの自動表示 / 非表示 =====
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<number | null>(null);
  const bumpControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(
      () => setControlsVisible(false),
      3200,
    );
  }, []);

  useEffect(() => {
    bumpControls();
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, [bumpControls]);

  // 一時停止中は常に表示する（操作の起点を見失わないように）。
  const showControls = controlsVisible || !isPlaying;

  // ===== シーク =====
  const seekToClientX = useCallback(
    (clientX: number) => {
      const bar = seekBarRef.current;
      if (!bar || !duration) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      onSeek(ratio * duration);
    },
    [duration, onSeek],
  );

  const handleSeekPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      seekToClientX(e.clientX);
    },
    [seekToClientX],
  );

  const handleSeekPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.buttons === 0) return;
      seekToClientX(e.clientX);
    },
    [seekToClientX],
  );

  const handleSeekPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.releasePointerCapture(e.pointerId);
    },
    [],
  );

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return createPortal(
    <Overlay
      ref={overlayRef}
      $hideCursor={!showControls}
      onPointerMove={bumpControls}
      onPointerDown={bumpControls}
      role="dialog"
      aria-label="鑑賞モード"
    >
      {track.thumbnailSrc && (
        <Backdrop
          style={{ backgroundImage: `url(${track.thumbnailSrc})` }}
          aria-hidden
        />
      )}

      {/* 中央: ジャケット + 作品名 / アーティスト名 */}
      <Stage>
        {track.thumbnailSrc ? (
          <Artwork src={track.thumbnailSrc} alt="" />
        ) : (
          <GenerativeBox>
            <GenerativeAudioThumbnail
              src={track.src}
              waveformUrl={track.waveformUrl}
              isPlaying={isPlaying}
              getCurrentTime={getCurrentTime}
              readSpectrum={readSpectrum}
            />
          </GenerativeBox>
        )}
        <TitleBlock>
          <span
            className="heading-spacing-18"
            style={{ color: "#fff", lineHeight: "150%" }}
          >
            {track.title}
          </span>
          {track.creatorName && (
            <span
              className="bodytext-13 u-line-height-100"
              style={{ color: "rgba(255, 255, 255, 0.64)" }}
            >
              {track.creatorName}
            </span>
          )}
        </TitleBlock>
      </Stage>

      {/* テロップ */}
      {activeCueText && (
        <SubtitleTelop $position={position}>{activeCueText}</SubtitleTelop>
      )}

      {/* 右上: 終了 */}
      <TopControls $visible={showControls}>
        <IconButton
          icon="close"
          size={26}
          color="rgba(255, 255, 255, 0.8)"
          hoverColor="#fff"
          onClick={onClose}
          title="鑑賞モードを終了"
          ariaLabel="鑑賞モードを終了"
        />
      </TopControls>

      {/* 下部: 再生コントロール + 字幕設定 */}
      <ControlLayer $visible={showControls}>
        <ControlPanel>
          <ControlRow>
            <div className="u-row u-gap8" style={{ flexShrink: 0 }}>
              <IconButton
                icon="skip_previous"
                filled
                size={28}
                color="rgba(255, 255, 255, 0.8)"
                hoverColor="#fff"
                disabled={!hasPrev}
                onClick={onPrev}
                ariaLabel="前のトラック"
              />
              <IconButton
                icon={isPlaying ? "pause" : "play_arrow"}
                filled
                size={36}
                color="#fff"
                hoverColor="#fff"
                onClick={onTogglePlay}
                ariaLabel={isPlaying ? "一時停止" : "再生"}
              />
              <IconButton
                icon="skip_next"
                filled
                size={28}
                color="rgba(255, 255, 255, 0.8)"
                hoverColor="#fff"
                disabled={!hasNext}
                onClick={onNext}
                ariaLabel="次のトラック"
              />
            </div>

            {/* 字幕トグル + 位置選択（字幕がある作品のみ） */}
            {hasCues && (
              <SubtitleControls>
                <IconButton
                  icon={subtitleEnabled ? "subtitles" : "subtitles_off"}
                  size={22}
                  color={subtitleEnabled ? "#fff" : "rgba(255, 255, 255, 0.45)"}
                  hoverColor="#fff"
                  onClick={() => setSubtitleEnabled((v) => !v)}
                  title={subtitleEnabled ? "字幕をオフ" : "字幕をオン"}
                  ariaLabel="字幕の表示切り替え"
                />
                {subtitleEnabled &&
                  POSITION_OPTIONS.map((option) => (
                    <PositionChip
                      key={option.value}
                      type="button"
                      $active={position === option.value}
                      onClick={() => handleSelectPosition(option.value)}
                      aria-pressed={position === option.value}
                    >
                      {option.label}
                    </PositionChip>
                  ))}
              </SubtitleControls>
            )}
          </ControlRow>

          <SeekBarContainer
            ref={seekBarRef}
            onPointerDown={handleSeekPointerDown}
            onPointerMove={handleSeekPointerMove}
            onPointerUp={handleSeekPointerUp}
            aria-label="再生位置"
          >
            <SeekBarTrack>
              <SeekBarFill style={{ width: `${progressPercent}%` }} />
            </SeekBarTrack>
            <SeekBarThumb style={{ left: `${progressPercent}%` }} />
          </SeekBarContainer>
          <TimeRow>
            <span>{formatTimecode(currentTime)}</span>
            <span>{formatTimecode(duration)}</span>
          </TimeRow>
        </ControlPanel>
      </ControlLayer>
    </Overlay>,
    document.body,
  );
};

export default AppreciationMode;
